import moment from 'moment';

moment.updateLocale('wm', {
	week: {
		dow: 6
	}
});

export const _wm = () => {
	const dayLowerCase = 'day';
	const daysLowerCase = 'days';
	const weekLowerCase = 'week';
	const weeksLowerCase = 'weeks';
	const quarterLowerCase = 'quarter';
	const monthLowerCase = 'month';
	const yearLowerCase = 'year';
	moment.locale('wm');
	const getMaxForMTS = (min, isDaily) => {
		let newMax = moment(min).add(12, weekLowerCase);
		if (isDaily) {
			newMax = newMax.endOf(weekLowerCase);
		}
		const currentDate = moment();
		// return the earliest date
		if (currentDate.isBefore(newMax)) {
			return currentDate;
		}
		return newMax;
	};

	const getCurrentQuarterStartDate = () => {
		const fiscalYearStartDate = moment()
			.clone()
			.startOf(yearLowerCase)
			.add(1, monthLowerCase)
			.startOf(weekLowerCase);

		const end = moment();
		while (end.diff(fiscalYearStartDate) >= 0) {
			fiscalYearStartDate.add(13, weeksLowerCase);
		}

		return fiscalYearStartDate.subtract(13, weeksLowerCase);
	};

	const getCurrentQuarterEndDate = () => {
		const currentQuarterStartDate = getCurrentQuarterStartDate();
		return currentQuarterStartDate.add(13, weeksLowerCase).subtract(1, dayLowerCase);
	};

	const getLastXWkRange = (x, forShrink) => {
		if (forShrink) {
			let start = moment().clone().subtract(x, weekLowerCase).startOf(weekLowerCase);
			let end = moment().clone().subtract(1, weekLowerCase).endOf(weekLowerCase);
			return [start, end];
		}

		return [`${x} weeks ago for ${x} weeks`];
	};

	const getNextXWkRange = (x, forShrink) => {
		if (forShrink) {
			let start = moment().clone().add(1, weekLowerCase).startOf(weekLowerCase);
			let end = moment().clone().add(x, weekLowerCase).endOf(weekLowerCase);
			return [start, end];
		}

		return [`1 weeks from now for ${x} weeks`];
	};

	const getMonthToDate = (forShrink) => {
		if (forShrink) {
			// start of week should always be the startDate for preview MTD
			const end = moment().clone().subtract(1, dayLowerCase);
			return [end.clone().startOf(weekLowerCase), end];
		}
		return [0, 0];
	};

	const getActualMonthToDate = (forShrink) => {
		if (forShrink) {
			const wmtMonthStart = moment().clone().startOf(monthLowerCase).startOf(weekLowerCase);
			const wmtNextMonthStart = moment()
				.clone()
				.add(1, monthLowerCase)
				.startOf(monthLowerCase)
				.startOf(weekLowerCase);
			const wmtMonthEnd = wmtNextMonthStart.clone().subtract(1, dayLowerCase);
			const end = moment().clone().subtract(1, dayLowerCase);
			// Subtracting 1 day from start date and adding 1 day to end date because isBetween checks for
			// current date > start date && current date < end date (which doesnt include 1st and 31st)
			return moment().isBetween(
				wmtMonthStart.clone().subtract(1, dayLowerCase),
				wmtMonthEnd.clone().add(1, dayLowerCase)
			)
				? [wmtMonthStart, end]
				: [wmtNextMonthStart, end];
		}
		return [0, 0];
	};

	const getLastXMonthRange = (x, forShrink) => {
		if (forShrink) {
			const [wmtStart] = getActualMonthToDate(true);
			let start = wmtStart.clone();
			for (let i = 0; i < x; i++) {
				start = start.subtract(1, dayLowerCase).startOf(monthLowerCase).startOf(weekLowerCase);
			}
			const end = wmtStart.subtract(1, dayLowerCase);
			return [start, end];
		}
		return [-x, -1];
	};

	const getLastXQtrRange = (x, forShrink) => {
		if (forShrink) {
			let offset = 0;
			const currMonth = moment().month();
			const startQtrMonth = moment().startOf(quarterLowerCase).month();

			if (startQtrMonth === currMonth) {
				offset = 1;
			}

			const startOffsetWeeks = (x + offset) * 13;
			const start = getCurrentQuarterStartDate().clone().subtract(startOffsetWeeks, weekLowerCase);
			const endOffsetWeeks = (1 + offset) * 13;
			const end = getCurrentQuarterEndDate().clone().subtract(endOffsetWeeks, weeksLowerCase);

			return [start, end];
		}

		return [-x, -1];
	};

	const getLastYrRange = (forShrink, prevToPrevYear = false) => {
		if (forShrink) {
			const start = moment()
				.clone()
				.subtract(prevToPrevYear ? 2 : 1, yearLowerCase)
				.startOf(yearLowerCase)
				.add(1, monthLowerCase)
				.startOf(weekLowerCase);
			const end = moment()
				.clone()
				.subtract(prevToPrevYear ? 2 : 1, yearLowerCase)
				.endOf(yearLowerCase)
				.add(1, monthLowerCase)
				.startOf(weekLowerCase)
				.subtract(1, daysLowerCase);
			return [start, end];
		}

		return [-1, -1];
	};

	const getWkToDate = (forShrink) => {
		if (forShrink) {
			let start = moment().clone().startOf(weekLowerCase);
			let end = moment().clone().subtract(1, dayLowerCase);
			return [start, end];
		}
		return ['1 weeks'];
	};

	const getCurrWkDate = (forShrink) => {
		if (forShrink) {
			let start = moment().clone().startOf(weekLowerCase);
			let end = moment().clone().endOf(weekLowerCase);
			return [start, end];
		}
		return ['1 weeks'];
	};

	const getCurrWkDatev1 = (forShrink) => {
		if (forShrink) {
			let start = moment().clone().startOf(weekLowerCase);
			let end = moment().clone().endOf(weekLowerCase);
			return [start, end];
		}
		return [0, 0];
	};

	const getNextXWkRangev1 = (x, forShrink) => {
		if (forShrink) {
			let start = moment().clone().add(1, weekLowerCase).startOf(weekLowerCase);
			let end = moment().clone().add(x, weekLowerCase).endOf(weekLowerCase);
			return [start, end];
		}

		return [1, x];
	};

	const getQtrToDate = (forShrink) => {
		if (forShrink) {
			const start = getCurrentQuarterStartDate();
			const end = moment().clone().subtract(1, dayLowerCase);
			return [start, end];
		}

		return [0, 0];
	};

	const getYrToDate = (forShrink) => {
		if (forShrink) {
			const start = moment()
				.clone()
				.startOf(yearLowerCase)
				.add(1, monthLowerCase)
				.startOf(weekLowerCase);
			const end = moment().clone().subtract(1, dayLowerCase);
			return [start, end];
		}

		return [0, 0];
	};

	const yesterday = (forShrink) => {
		if (forShrink) {
			let start = moment().clone().subtract(1, dayLowerCase);
			let end = moment().clone().subtract(1, dayLowerCase);
			return [start, end];
		}

		return [`1 days ago for 1 days`];
	};

	const today = (forShrink) => {
		if (forShrink) {
			let start = moment().clone();
			let end = moment().clone();
			return [start, end];
		}

		return [`1 days`];
	};

	const getWeekendRange = (forShrink) => {
		if (forShrink) {
			let start = moment().clone().set(dayLowerCase, -2);
			let end = moment().clone().set(dayLowerCase, 0);
			return [start, end];
		}
		return { 'vw_weekend_reporting.cal_dt': 'NOT NULL' };
	};

	const getLastYearXWkRange = (x, forShrink) => {
		const currentWeekStart = moment().clone().startOf(weekLowerCase);
		const currentWeekEnd = moment().clone().endOf(weekLowerCase);
		const lastYearStart = currentWeekStart
			.clone()
			.subtract(1, yearLowerCase)
			.subtract(x - 1, weekLowerCase)
			.startOf(weekLowerCase);
		const lastYearEnd = currentWeekEnd.clone().subtract(1, yearLowerCase).endOf(weekLowerCase);
		if (forShrink) {
			return [lastYearStart, lastYearEnd];
		}
		return [lastYearStart.format('YYYY-MM-DD'), lastYearEnd.format('YYYY-MM-DD')];
	};

	const getLastLastYearXWkRange = (x, forShrink) => {
		const currentWeekStart = moment().clone().startOf(weekLowerCase);
		const currentWeekEnd = moment().clone().endOf(weekLowerCase);
		const lastLastYearStart = currentWeekStart
			.clone()
			.subtract(2, yearLowerCase)
			.subtract(x - 1, weekLowerCase)
			.startOf(weekLowerCase);
		const lastLastYearEnd = currentWeekEnd.clone().subtract(2, yearLowerCase).endOf(weekLowerCase);
		if (forShrink) {
			return [lastLastYearStart, lastLastYearEnd];
		}
		return [lastLastYearStart.format('YYYY-MM-DD'), lastLastYearEnd.format('YYYY-MM-DD')];
	};

	const getLastXYearRange = (x, forShrink) => {
		const start = moment().clone().subtract(x, yearLowerCase).startOf(weekLowerCase);
		const end = moment().clone().subtract(1, dayLowerCase);
		if (forShrink) {
			return [start, end];
		}
		return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
	};

	return {
		getLastXWkRange,
		getWkToDate,
		yesterday,
		getMonthToDate,
		getActualMonthToDate,
		getLastXMonthRange,
		getLastXQtrRange,
		getQtrToDate,
		getLastYrRange,
		getYrToDate,
		getNextXWkRange,
		getCurrWkDate,
		getCurrWkDatev1,
		getNextXWkRangev1,
		today,
		getWeekendRange,
		getMaxForMTS,
		getLastYearXWkRange,
		getLastLastYearXWkRange,
		getLastXYearRange
	};
};
